import { Avatar, Flex, Group, Paper, Stack, Text } from '@mantine/core';

import Button from '@/components/content/button/Button';
import MercurySkeleton from '@/components/content/placeholder/Skeleton';
import { useAuth } from '@/hooks/useAuth';

import { formatIsoDate } from '@/utils/date';
import { contractRangeFromTrials } from '../../utils/contractRange';
import { isTrialEndingSoon } from '../../utils/isTrialEndingSoon';
import { formatModuleDisplayName, isDisplayableEntitlement } from '../../utils/moduleDisplayName';
import { gslSectionsFromClient } from '../../utils/gslSections';
import { parseClientId, useGetClient } from '../../services/getClient';
import GslCards from '../gsl/GslCards';

import FileIcon from '@/assets/icons/content/file.svg';
import UserIcon from '@/assets/icons/content/user.svg';
import EmailIcon from '@/assets/icons/content/email.svg';
import CheckOutlineIcon from '@/assets/icons/content/check-outline.svg';
import TimeTimelapseIcon from '@/assets/icons/content/time-timelapse.svg';

import classes from './SubscriptionsInfo.module.css';

const getInitials = (name: string): string =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');

type ModuleListItem = {
  name: string;
  date?: string;
  endingSoon?: boolean;
};

const formatModuleDate = (value: string | null): string =>
  formatIsoDate(value ?? '', true).replaceAll('-', '/');

const ModulesCard = ({
  title,
  endingHeader,
  icon: Icon,
  items,
}: {
  title: string;
  endingHeader?: boolean;
  icon: typeof CheckOutlineIcon;
  items: ModuleListItem[];
}) => {
  if (items.length === 0) return null;

  return (
    <Paper
      withBorder
      shadow="none"
      radius={2}
      p={16}
      className={`${classes.card} ${classes.equalCard}`}
    >
      <Stack gap={20}>
        <Group justify="space-between" wrap="nowrap" className={classes.modulesHeader}>
          <Text className={classes.cardTitle}>{title}</Text>
          {endingHeader && <Text className={classes.endingLabel}>Ending</Text>}
        </Group>
        <Stack gap={20} component="ul" aria-label={title} className={classes.modulesList}>
          {items.map((item) => (
            <Group
              key={item.name}
              component="li"
              gap={10}
              wrap="nowrap"
              className={classes.moduleRow}
            >
              <Icon className={classes.moduleIcon} aria-hidden="true" />
              <Text span className={classes.moduleName}>
                {item.name}
              </Text>
              {item.date !== undefined && (
                <Text
                  span
                  className={`${classes.moduleDate}${item.endingSoon ? ` ${classes.moduleDateEndingSoon}` : ''}`}
                >
                  {item.date}
                </Text>
              )}
            </Group>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
};

const SubscriptionsInfo = () => {
  const { userInfo, tokenInfo } = useAuth();
  const clientId = parseClientId(tokenInfo.client_access_id);
  const { data: client, isLoading, isError, refetch } = useGetClient(clientId);
  const trials = userInfo.entitlements_trial
    .filter((t) => !t.is_expired)
    .sort((a, b) => (a.end_at ?? '').localeCompare(b.end_at ?? ''));
  const trialNames = new Set(trials.map((trial) => trial.name));
  const subscribed = (tokenInfo.entitlements ?? []).filter(
    (name) => isDisplayableEntitlement(name) && !trialNames.has(name)
  );
  const orgName = tokenInfo.client_name?.trim() || 'Organisation';
  const orgInitials = getInitials(orgName);
  const contactName =
    [tokenInfo.first_name, tokenInfo.last_name].filter((value) => value?.trim()).join(' ') ||
    tokenInfo.sub;
  const { start: contractStart, end: contractEnd } = contractRangeFromTrials(
    userInfo.entitlements_trial
  );

  return (
    <Stack gap={20}>
      <Flex className={classes.cardsRow} gap={16} align="stretch" wrap="nowrap">
        <Paper
          withBorder
          shadow="none"
          radius={2}
          p={16}
          className={`${classes.card} ${classes.equalCard}`}
        >
          <Group gap={26} align="flex-start" wrap="nowrap">
            <Avatar size={114} radius={114} className={classes.avatar}>
              {orgInitials}
            </Avatar>

            <Stack gap={16} className={classes.orgDetails}>
              <Text className={classes.orgName}>{orgName}</Text>

              <Group gap={6} wrap="nowrap" className={classes.infoRow}>
                <FileIcon aria-hidden="true" />
                <Text span>
                  Contract start: {formatIsoDate(contractStart, true)}
                  {' - end: '}
                  {formatIsoDate(contractEnd, true)}
                </Text>
              </Group>

              <Group gap={6} wrap="nowrap" className={classes.infoRow}>
                <UserIcon aria-hidden="true" />
                <Text span>{contactName}</Text>
              </Group>

              <Group gap={6} wrap="nowrap" className={classes.infoRow}>
                <EmailIcon aria-hidden="true" />
                <Text span>{tokenInfo.sub}</Text>
              </Group>
            </Stack>
          </Group>
        </Paper>

        <ModulesCard
          title="Modules subscribed to:"
          icon={CheckOutlineIcon}
          items={subscribed.map((name) => ({
            name: formatModuleDisplayName(name),
          }))}
        />

        {trials.length > 0 && (
          <ModulesCard
            title="Modules on trial"
            endingHeader
            icon={TimeTimelapseIcon}
            items={trials.map((trial) => ({
              name: formatModuleDisplayName(trial.name),
              date: formatModuleDate(trial.end_at),
              endingSoon: isTrialEndingSoon(trial.end_at),
            }))}
          />
        )}
      </Flex>

      {isLoading && <MercurySkeleton n={1} id="client-gsl" height={180} />}
      {isError && (
        <Group gap={12} wrap="nowrap">
          <Text>Unable to load organisation profile.</Text>
          <Button onClick={() => refetch()}>Retry</Button>
        </Group>
      )}
      {client && <GslCards sections={gslSectionsFromClient(client)} />}
    </Stack>
  );
};

export default SubscriptionsInfo;
